import { FC } from 'react'
import { Button, Paper } from '@expane/ui'
import { useTranslation } from 'react-i18next'
import {
  AppSubscriptionProduct,
  calculateYearlySavings,
  convertPaddleAmount,
  getMonthlyPrice,
  getYearlyPrice,
  PlanType,
} from '@expane/logic/appSubscription'

export type ServerPlanType = {
  maxEmployees: number | null
  type: string
}

interface ProductCardProps {
  product: AppSubscriptionProduct
  planTypes: ServerPlanType[]
  onPriceSelected?: (priceId: string, planType: PlanType) => void
  onPlanTypeSelected?: (planType: PlanType) => void
  activePlanType?: string
  disabled?: boolean
  buyButtonText?: string
}

export const AppSubscriptionProductCard: FC<ProductCardProps> = ({
  product,
  activePlanType,
  planTypes,
  onPriceSelected,
  onPlanTypeSelected,
  disabled,
  buyButtonText,
}) => {
  const { t } = useTranslation()

  // Find monthly and yearly prices
  const monthlyPrice = getMonthlyPrice(product)

  const yearlyPrice = getYearlyPrice(product)

  const productPlanType = product.customData.planType

  const maxEmployees = planTypes.find(({ type }) => type === productPlanType)?.maxEmployees

  const isActive = activePlanType ? activePlanType === productPlanType : false

  return (
    <div
      className={`p-4 w-1/3 max-w-xl border rounded-lg ${
        isActive ? 'border-primary-400' : 'border-block-color '
      }`}
    >
      {/* Product Header */}
      <div className="mb-4">
        <h3 className="text-xl font-bold text-main-color">{t('planType.' + productPlanType)}</h3>
        {product.description && (
          <p className="mt-2 text-sm text-secondary-color">{product.description}</p>
        )}
      </div>

      {/* Pricing Options */}
      <div className="flex flex-col gap-4">
        {monthlyPrice && (
          <Paper className="p-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-secondary-color">
                {t('appSubscription.interval.monthly')}
              </span>
              <span className="text-lg font-bold text-main-color">
                {convertPaddleAmount(monthlyPrice.unitPrice.amount)}
              </span>
            </div>
            {monthlyPrice.description && (
              <p className="mt-1 text-xs text-secondary-color">{monthlyPrice.description}</p>
            )}
            {Boolean(onPriceSelected) && (
              <Button
                type="outline"
                className="w-full mt-4"
                onClick={() => onPriceSelected?.(monthlyPrice.id, product.customData.planType)}
                disabled={disabled}
              >
                {buyButtonText ? buyButtonText : t('buy')}
              </Button>
            )}
          </Paper>
        )}

        {yearlyPrice && (
          <div className="mb-4 p-4 bg-primary-100/10 border border-block-color rounded-lg ">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-secondary-color">
                {t('appSubscription.interval.yearly')}
              </span>
              <span className="text-lg font-bold text-main-color">
                {convertPaddleAmount(yearlyPrice.unitPrice.amount)}
              </span>
            </div>
            {yearlyPrice.description && (
              <p className="mt-1 text-xs text-secondary-color">{yearlyPrice.description}</p>
            )}
            {monthlyPrice && (
              <p className="mt-2 text-xs text-primary-500 font-medium">
                {t('appSubscription.save')}{' '}
                {calculateYearlySavings(
                  monthlyPrice.unitPrice.amount,
                  yearlyPrice.unitPrice.amount,
                )}
                %
              </p>
            )}
            {Boolean(onPriceSelected) && (
              <Button
                type="primary"
                className="w-full mt-4"
                onClick={() => onPriceSelected?.(yearlyPrice.id, product.customData.planType)}
                disabled={disabled}
              >
                {buyButtonText ? buyButtonText : t('buy')}
              </Button>
            )}
          </div>
        )}
      </div>

      <MaxEmployees maxEmployees={maxEmployees} />

      {Boolean(onPlanTypeSelected) && !isActive && (
        <Button
          type="outline"
          className="w-full mt-4"
          onClick={() => onPlanTypeSelected?.(product.customData.planType)}
          disabled={disabled}
        >
          {t('choose')}
        </Button>
      )}
    </div>
  )
}

const MaxEmployees: FC<{ maxEmployees: number | undefined | null }> = ({ maxEmployees }) => {
  const { t } = useTranslation()

  if (typeof maxEmployees === 'undefined') return null

  const textStyle = 'text-secondary-color text-xs'
  const accentTextStyle = 'text-primary-500'

  if (maxEmployees === null)
    return <p className={textStyle}>{t('appSubscription.maxEmployees.unlimited')}</p>
  if (maxEmployees === 1)
    return (
      <p className={textStyle}>
        {t('appSubscription.maxEmployees.basic.1')}{' '}
        <span className={accentTextStyle}>{t('appSubscription.maxEmployees.basic.2')}</span>
      </p>
    )

  return (
    <p className={textStyle}>
      {t('appSubscription.maxEmployees.professional.1')}{' '}
      <span className={accentTextStyle}>{maxEmployees}</span>{' '}
      {t('appSubscription.maxEmployees.professional.2')}
    </p>
  )
}
